/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// fortee-logo-pipeline.js - the shared logo pipeline used by BOTH builders:
// upload -> alpha detect -> automatic background removal -> auto-crop ->
// "Preparing your artwork" overlay -> the "Logo added" card getters -> the
// Edit fork open/close.
//
// MOVED (not copied) out of bundle-builder-2step.js by quick task 260807-lb5.
// The method bodies are the 2-step's, unchanged except for the four seams
// documented below, so the 2-step keeps behaving identically while the single
// builder mounts the SAME implementation. There is no second copy anywhere.
//
// Usage - spread both into the Alpine component:
//     Object.assign(component, window.forteeLogoPipelineState(), window.forteeLogoPipeline());
//
// ADAPTER CONTRACT - the host builder MUST provide these. The pipeline touches
// builder-specific artwork state ONLY through them:
//
//   _pipelineArtwork(key)                  -> artwork object for key, or null
//   _pipelineWriteArtwork(key, obj, opts)  -> write artwork for key.
//        opts.cascade === false : write WITHOUT re-sharing to sibling keys, but
//                                 still invalidate caches/previews.
//        opts.quiet   === true  : write with NO invalidation at all (used by the
//                                 lazy cascade inside _ctxFor, which Alpine may
//                                 evaluate during render).
//   _pipelineDeleteArtwork(key)            -> clear artwork for key + invalidate
//                                             (the shared card's delete button)
//   _pipelinePrimaryArtwork()              -> artwork the prep screen previews
//   _pipelineCascadeSource(view)           -> artwork an EMPTY row lazily inherits,
//                                             or null when rows do not share a logo
//   _viewForLocKey(productId, locKey)      -> view key for an artwork row
//   _activeArtworkContext(pid, view)       -> { product, color, view, artwork }
//   _activeViewForProduct(productId)       -> view key
//   _artworkUrlFromObj(artwork)            -> url string
//   _autoVersionKey(product, color, art)   -> version key string
//   _VERSION_LABELS                        -> { [versionKey]: label }
//   _designerReviewRequested               -> { [view]: true }
//   _generateVersions(key)                 -> build the logo colour versions
//   _buildPrePlacedTiles()                 -> re-render previews after a write
//   _mockup2Cache                          -> composited-preview cache
//   _rowPlacementLabel(productId, locKey)  -> human label for an artwork row
//   _prepAfterContinue()                   -> what "Continue" does after prep
//   _prepReviewTarget()                    -> { pid, key } for "Review artwork"
//   _t(key, fallback)                      -> i18n lookup
//   showToast(msg, ok)                     -> user feedback
//
// The host must ALSO spread window.forteeLogoEditor() - _prepMaybeDone routes a
// "new logo" upload into the editor's _openScope(), and the Edit fork's buttons
// call the editor's openEditor() / startUploadNew().
//
// THE WRITE SEAM (why _pipelineWriteArtwork exists): the 2-step's groupArtwork is
// a plain component object, so `this.groupArtwork[k] = obj` is reactive. The
// single builder's groupArtwork is a GETTER onto Alpine.store('builder').perViewArtwork
// and it detects change by REPLACING that object - an in-place key write would be
// invisible to its $watch. Every artwork write therefore goes through the host.

(function () {
  'use strict';

  // Client-side abort for the remove-bg proxy. The fortee-api proxy polls
  // Uploadcare for up to ~55 s; aborting at 20 s flagged good removals as
  // needs-review and baked the full-frame original (quick 260915-86c).
  var BG_REMOVE_TIMEOUT_MS = 60000;

  // Pipeline-owned reactive state. Kept separate from the methods so a host can
  // merge it without hand-declaring the fields.
  window.forteeLogoPipelineState = function forteeLogoPipelineState() {
    return {
      _bgRemovingGroup: null,                   // groupKey currently being processed by remove-bg API
      _bgRemoveSuccess: false,                  // brief "Background removed!" confirmation flag
      // Durable for the life of ONE upload: true only once the remove-bg API
      // actually returned a stripped image. _bgRemoveSuccess cannot be reused
      // for this — it self-clears after 3s, and the "your original file is
      // safe" reassurance has to stay on screen for as long as the customer is
      // looking at the preview that alarmed them.
      _bgRemovalRan: false,

      // Same lifetime as _bgRemovalRan, for the OTHER way the preview stops
      // matching the upload: an already-transparent file skips the remove-bg
      // API entirely but is still cropped to its content bounds, so it comes
      // back visibly tighter. That path shipped with no explanation at all,
      // which is the case customers actually hit first — most people upload a
      // transparent PNG. Honest only because _trimAndStore resolves true only
      // when a crop was actually stored, so this is never true for a crop that
      // did not happen.
      _artworkTrimmed: false,
      _bgPreference: 'transparent',             // 'transparent' | 'original' (default: transparent per spec Phase 2)
      _colorCheckRequired: false,               // set true when uploaded logo has light tones (luminance > 0.85 on >5% of pixels)
      // Step 1.5 — "Preparing your artwork" loading overlay (spec §1.5 / prototype)
      _prepOpen: false,
      _prepDone: false,                         // show the "Your logo is ready" panel
      _prepReady: false,                        // processing actually finished
      _prepAnimDone: false,                     // the checkmark animation finished
      _prepSteps: [],                           // [{label, state:'pending'|'doing'|'done'}]
      _prepLastStepAt: 0,                       // Date.now() at which the final step starts spinning
      _prepTimers: [],
      _prepAfter: 'initial',                    // 'initial' → ready panel; 'newlogo' → scope selector
      _prepUploadGroup: null,                   // group key of the in-flight upload (for newlogo scope)
      // Which row opened the Edit fork. _artworkEditFor holds a PRODUCT id, which
      // the single builder does not have — so the fork's own x-show is gated on
      // this key instead, and a host with no product id can still open it.
      _artworkEditKey: null,
    };
  };

  window.forteeLogoPipeline = function forteeLogoPipeline() {
    return {

      // =====================================================================
      // Canvas primitives
      // =====================================================================

      // Load an image with CORS into a canvas. Caps source dimensions so the
      // pixel-scan loops stay bounded. Resolves null on load/CORS/timeout failure
      // so callers can fall through to the API path instead of stalling.
      //
      // maxDim defaults to 1200 - the ANALYSIS resolution, and every existing
      // call site relies on that default being unchanged. Pass a larger value
      // when the canvas is the source of a PRINT artefact rather than of a
      // decision: keying or unioning on the 1200 cap would quietly swap a
      // full-resolution print file for a downscaled one, unnoticed until
      // production (quick 260819-bb8, PLAN.md Risk 3). 0 means uncapped.
      _loadImageToCanvas: function (url, maxDim) {
        var cap = (typeof maxDim === 'number') ? maxDim : 1200;
        return new Promise(function (resolve) {
          if (!url) { resolve(null); return; }
          var img = new Image();
          img.crossOrigin = 'anonymous';
          var settled = false;
          var timeoutId = setTimeout(function () {
            if (settled) return;
            settled = true;
            resolve(null);
          }, 8000);
          img.onload = function () {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            try {
              var nw = img.naturalWidth || 1;
              var nh = img.naturalHeight || 1;
              var scale = cap > 0 ? Math.min(1, cap / Math.max(nw, nh)) : 1;
              var canvas = document.createElement('canvas');
              canvas.width = Math.max(1, Math.round(nw * scale));
              canvas.height = Math.max(1, Math.round(nh * scale));
              var ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve({ canvas: canvas, ctx: ctx });
            } catch (e) {
              resolve(null);
            }
          };
          img.onerror = function () {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(null);
          };
          img.src = url;
        });
      },

      // Returns true when >2% of pixels have alpha < 250 — i.e., the upload
      // already carries meaningful transparency, so the bg-remove API is a no-op.
      _canvasHasAlpha: function (canvas, ctx) {
        try {
          var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          var transparent = 0;
          var total = canvas.width * canvas.height;
          for (var i = 3; i < data.length; i += 4) {
            if (data[i] < 250) transparent++;
          }
          return total > 0 && (transparent / total) > 0.02;
        } catch (e) {
          return false;
        }
      },

      // Uploadcare public key for whichever builder hosts the pipeline.
      _uploadPubkey: function () {
        return (window.bundleData && window.bundleData.uploadcarePubkey) ||
          (window.productData && window.productData.uploadcarePubkey) ||
          (window.forteeShared && window.forteeShared._uploadcarePubkey) || '';
      },

      // The file versions (outline, recolours) and bakes are built from: the
      // stored https crop of whichever source is current. A data: crop left in
      // an older saved state is never used, so it can never reach a record.
      _versionSourceUrl: function (art) {
        if (!art) return null;
        var https = function (u) { return (typeof u === 'string' && /^https:\/\//i.test(u)) ? u : null; };
        if (art.bgRemovedUrl) return https(art.bgRemovedCroppedUrl) || art.bgRemovedUrl;
        return https(art.originalCroppedUrl) || art.url || art.originalUrl || null;
      },

      // Trim ONE artwork source with the shared content box
      // (assets/fortee-content-box.js via forteeShared.trimArtwork), host the
      // crop on Uploadcare and MERGE it into the record together with a trim
      // record { src, box (fractions), srcW, srcH, version, reason }. This is the
      // only place a crop is stored, so the preview, the aspect measurement, the
      // versions and the bake all read the same box.
      //
      //   which  'original' | 'bgRemoved' | 'both' (an already-transparent upload
      //          is both at once and is trimmed once)
      //
      // The uploaded file carries a 1px transparent frame (forteeShared.framedPng):
      // it is what keeps a later re-trim of the stored crop from cutting into
      // opaque art - see frameRgba in fortee-content-box.js.
      //
      // Resolves true only when an https crop was written. Never rejects.
      _trimAndStore: function (groupKey, sourceUrl, which) {
        var self = this;
        var shared = window.forteeShared;
        if (!sourceUrl || !shared || typeof shared.trimArtwork !== 'function') return Promise.resolve(false);
        var wantOriginal = which === 'original' || which === 'both';
        var wantBg = which === 'bgRemoved' || which === 'both';

        return Promise.resolve(shared.trimArtwork(sourceUrl)).then(function (t) {
          if (!t) return null;
          var trim = {
            src: sourceUrl, box: t.fractions, srcW: t.srcW, srcH: t.srcH,
            version: t.version, reason: t.reason
          };
          if (!t.box) return { croppedUrl: null, trim: trim };
          if (typeof shared.framedPng !== 'function' || typeof shared.uploadToUploadcare !== 'function') {
            console.warn('[FORTEE pipeline] no crop uploader; artwork kept uncropped', { groupKey: groupKey, sourceUrl: sourceUrl });
            return null;
          }
          return Promise.resolve(shared.uploadToUploadcare(shared.framedPng(t.canvas), self._uploadPubkey())).then(function (hosted) {
            if (typeof hosted !== 'string' || !/^https:\/\//i.test(hosted)) {
              console.warn('[FORTEE pipeline] crop upload did not return an https url; artwork kept uncropped',
                { groupKey: groupKey, sourceUrl: sourceUrl });
              return null;
            }
            return { croppedUrl: hosted, trim: trim };
          });
        }).then(function (res) {
          if (!res) return false;
          var current = self._pipelineArtwork(groupKey);
          if (!current) return false;
          // The slot may have moved on while the trim and upload were in flight.
          if ((wantBg && current.bgRemovedUrl !== sourceUrl) ||
              (wantOriginal && (current.originalUrl || current.url) !== sourceUrl)) {
            return false;
          }
          var fields = {};
          if (wantOriginal) { fields.originalCroppedUrl = res.croppedUrl; fields.originalTrim = res.trim; }
          if (wantBg) { fields.bgRemovedCroppedUrl = res.croppedUrl; fields.bgRemovedTrim = res.trim; }
          self._pipelineWriteArtwork(groupKey, Object.assign({}, current, fields));
          self._generateVersions(groupKey);
          self._buildPrePlacedTiles();
          return !!res.croppedUrl;
        }).catch(function (err) {
          console.warn('[FORTEE pipeline] crop failed; artwork kept uncropped:', groupKey, err);
          return false;
        });
      },

      // =====================================================================
      // Automatic processing pipeline
      // =====================================================================

      // Record an upload in the customer's persistent Artwork Library
      // (quick 260816-9ik). This is the ONE recording hook for BOTH builders:
      // every upload in either builder reaches _processArtwork, so recording
      // here covers both instead of duplicating the call in each.
      //
      // Same contract as window.forteeDiag: NEVER throws and NEVER changes
      // control flow. A library that cannot be written is a lost convenience;
      // a library that can break the pipeline is a lost order.
      //
      // Callers pass CDN URL FIELDS ONLY — never an artwork object wholesale.
      // Artwork objects carry crops, trim records, versions and editor state the
      // library's closed schema does not describe; not handing them over is the
      // honest boundary.
      _recordInLogoLibrary: function (record) {
        try {
          if (!record || !record.url) return;
          if (typeof Alpine === 'undefined') return;
          var store = Alpine.store('builder');
          if (!store || typeof store.logoLibraryAdd !== 'function') return;
          store.logoLibraryAdd({
            url: record.url,
            bgRemovedUrl: record.bgRemovedUrl || null,
            filename: record.filename || ''
          });
        } catch (e) {
          /* the library is never allowed to break the upload it observes */
        }
      },

      // Entry point after upload: detect transparency, generate cropped variants,
      // and route to either the fast path (already transparent → no API call) or
      // the bg-remove API.
      _processArtwork: function (groupKey, cdnUrl, filename) {
        var self = this;
        if (!groupKey || !cdnUrl) return;
        // Analysis (alpha detect + crop) is local and quick — don't flag "removing".
        self._bgRemovingGroup = null;
        // Clear last upload's reassurance before either fork is chosen. This is
        // the only point guaranteed to precede BOTH the already-transparent fast
        // path and the API path; resetting in _startPrep would race, because the
        // hosts call it separately.
        self._bgRemovalRan = false;
        self._artworkTrimmed = false;

        // Record the raw upload immediately, before any async work. Background
        // removal can fail or time out; the customer's own file is still theirs
        // to reuse, so the library entry must not be contingent on it.
        self._recordInLogoLibrary({ url: cdnUrl, filename: filename });

        self._loadImageToCanvas(cdnUrl).then(function (loaded) {
          if (!loaded) {
            // Canvas read failed (CORS, decode error, timeout). We can't crop or
            // run the free client-side remover, but still offer the paid API.
            self._autoRemoveBg(groupKey, cdnUrl, filename).then(function (ok) {
              if (!ok) self._flagNeedsReview(groupKey, 'bg-removal-failed');
              self._buildPrePlacedTiles();
              self._prepMarkReady();
            });
            return;
          }

          var hasAlpha = self._canvasHasAlpha(loaded.canvas, loaded.ctx);

          if (hasAlpha) {
            // Already transparent — skip the API entirely. Original and
            // Transparent are the same file here, so one trim serves both.
            var existing = self._pipelineArtwork(groupKey);
            if (!existing) {
              self._bgRemovingGroup = null;
              return;
            }
            // Merge. The crop fields are cleared, not carried: they belong to
            // whatever was in this slot before, and _trimAndStore writes this
            // upload's own crop when it lands.
            self._pipelineWriteArtwork(groupKey, Object.assign({}, existing, {
              originalUrl: existing.originalUrl || cdnUrl,
              bgRemovedUrl: cdnUrl,
              originalCroppedUrl: null,
              bgRemovedCroppedUrl: null,
              originalTrim: null,
              bgRemovedTrim: null,
              filename: existing.filename || filename
            }));
            self._bgRemovingGroup = null;
            // Frames 03-05: light-tone detection — gate the color-check block.
            self._colorCheckRequired = self._detectLightTones(loaded.canvas, loaded.ctx);
            console.log('[FORTEE pipeline] already-transparent fast path for', groupKey,
              '— skipped bg-remove API; colorCheckRequired=', self._colorCheckRequired);
            self._generateVersions(groupKey);
            self._buildPrePlacedTiles();
            // No API call happens on this path, so the crop is the ONLY alteration
            // the customer sees. Ready waits for it; the prep overlay's 9 s safety
            // net still bounds a slow trim or upload.
            self._trimAndStore(groupKey, cdnUrl, 'both').then(function (trimmed) {
              self._artworkTrimmed = trimmed;
              self._prepMarkReady();
            });
            return;
          }

          // Solid background — trim the original now (the content box reads the
          // background colour itself), then call the API for the transparent
          // variant. If the API then succeeds, _bgRemovalRan outranks the trim in
          // originalSafeMode() and the customer reads the background sentence; if
          // it FAILS, the preview is still tighter than the file and this keeps
          // that explained rather than silently unexplained.
          self._trimAndStore(groupKey, cdnUrl, 'original').then(function (trimmed) {
            self._artworkTrimmed = trimmed;
          });

          // V2: background removal is AUTOMATIC now (no opt-in banner). Non-transparent
          // uploads go to the proxy (Photoroom → Uploadcare add-on fallback); the free
          // in-browser remover was dropped. Versions regenerate inside _applyBgRemoved.
          self._autoRemoveBg(groupKey, cdnUrl, filename).then(function (ok) {
            if (!ok) self._flagNeedsReview(groupKey, 'bg-removal-failed');
            self._buildPrePlacedTiles();
            self._prepMarkReady();
          });
        });
      },

      // Opt-in "Remove background?" banner removed 2026-07-12 — removal is automatic
      // (proxy: fal.ai → Uploadcare add-on). The banner is now progress/success only.

      // Apply a bg-removed URL (from either the client-side remover or the API):
      // write it onto the group, cascade to sibling groups, and crop it async.
      _applyBgRemoved: function (groupKey, cdnUrl, filename, bgUrl) {
        var self = this;
        var existing = self._pipelineArtwork(groupKey);
        if (!existing) { self._buildPrePlacedTiles(); return; }

        // THE RESULT BELONGS TO cdnUrl, NOT TO THE VIEW.
        //
        // Removal is async and the slot is addressed by view, so the customer
        // can replace the artwork while a removal is still in flight. Writing
        // the result without rechecking puts logo A's transparent cut onto
        // logo B's record: originalUrl then names one brand and
        // productionArtworkUrl names another, and production is what prints.
        // Order 7659 shipped that way — a real-estate logo recorded against a
        // volleyball club's badge — and the mockup agreed with the wrong half
        // because it is composited from the same resolved url.
        //
        // Dropping is the only safe answer: the customer keeps the artwork
        // they can see, and a removal they never saw cannot replace it. The
        // async crop below already guards itself exactly this way.
        if (existing.originalUrl !== cdnUrl && existing.url !== cdnUrl) {
          console.warn('[FORTEE pipeline] stale bg-remove result for "' + groupKey +
            '" — the slot moved on while it was in flight; dropped rather than swapping the artwork');
          self._buildPrePlacedTiles();
          return;
        }

        // MERGE onto the existing record. This used to rebuild it field by
        // field, so every removal or Touch-up save silently dropped whatever
        // the list forgot — editorJSON, analysis, versions, the edits copy.
        // editedFrom in particular has to survive, or the cart records an edit
        // as its own original.
        var fields = {
          url: existing.url,
          originalUrl: existing.originalUrl || cdnUrl,
          bgRemovedUrl: bgUrl,
          originalCroppedUrl: existing.originalCroppedUrl || null,
          bgRemovedCroppedUrl: existing.bgRemovedCroppedUrl || null,
          filename: existing.filename || filename,
          editedFrom: existing.editedFrom || null
        };
        // A NEW cut: the stored crop belongs to the previous one. Cleared in the
        // same write so no consumer ever pairs this file with another file's box.
        if (bgUrl !== existing.bgRemovedUrl) {
          fields.bgRemovedCroppedUrl = null;
          fields.bgRemovedTrim = null;
        }
        var updated = Object.assign({}, existing, fields);
        self._pipelineWriteArtwork(groupKey, updated);

        // Upsert the transparent version onto the SAME library row. Identity is
        // the original url, so this merges into the record _processArtwork
        // already created rather than adding a near-identical second tile.
        self._recordInLogoLibrary({
          url: updated.originalUrl,
          bgRemovedUrl: bgUrl,
          filename: updated.filename
        });

        // Frames 03-05: light-tone detection on the bg-removed image.
        self._loadImageToCanvas(bgUrl).then(function (loaded2) {
          if (!loaded2) return;
          self._colorCheckRequired = self._detectLightTones(loaded2.canvas, loaded2.ctx);
        });
        // Trim the cut once and merge the stored crop into the record.
        self._trimAndStore(groupKey, bgUrl, 'bgRemoved');

        self._generateVersions(groupKey);
        self._buildPrePlacedTiles();
      },

      // Repair a removal result by unioning its alpha with the soft content
      // alpha of the customer's original, then re-host the repaired PNG.
      // Resolves the hosted https url, or null when the union does not apply or
      // cannot be hosted - in which case the caller keeps the raw AI result.
      //
      // WHY. The engine does salient-object isolation: it keeps the single
      // biggest object and deletes every other element. The border-referenced
      // content measurement has the opposite failure - it keeps every disjoint
      // letter but holes any artwork shading that happens to sit at the
      // background colour. Measured on the Martel fixture: AI alone 27.1%
      // opaque, content alone 38.5%, union 47.0% and visually correct. Each half
      // is wrong in a way the other fixes.
      //
      // THE GATE lives in window.forteeBgRetention.canUnion and is enforced
      // inside unionWithContent: on a PHOTOGRAPHIC background the content
      // measurement marks nearly the whole frame as artwork, so unioning would
      // restore the background and silently disable removal. Measured on
      // synthetic noise, an ungated union puts back >96% of what a correct
      // removal cleared - and the retention guard ACCEPTS that result, so the
      // gate is the only thing preventing it. Never call this without it.
      //
      // PRINT RESOLUTION. Both loads use a 2400 cap rather than the 1200
      // analysis cap: this canvas becomes the artefact that prints (Risk 3).
      // The union is emitted on the ORIGINAL's grid with the ORIGINAL's rgb,
      // because engines commonly zero the colour of pixels they clear - raising
      // alpha on the engine's own pixels would restore black rectangles.
      //
      // A `data:` url must never escape this method. _safeUrl
      // (builder-shared-services.js) returns null for anything but http(s), and
      // both builders strip `data:` before persisting, so a data url would look
      // perfect for the whole session and then vanish from the cart.
      _unionBgCandidate: function (cdnUrl, bgUrl) {
        var self = this;
        var PRINT_MAX_DIM = 2400;
        var R = (typeof window !== 'undefined') ? window.forteeBgRetention : null;
        if (!R || typeof R.unionWithContent !== 'function' || !cdnUrl || !bgUrl) {
          return Promise.resolve(null);
        }

        return Promise.all([
          self._loadImageToCanvas(cdnUrl, PRINT_MAX_DIM),
          self._loadImageToCanvas(bgUrl, PRINT_MAX_DIM)
        ]).then(function (pair) {
          var orig = pair[0];
          var cand = pair[1];
          if (!orig || !cand) return null;

          var oi = orig.ctx.getImageData(0, 0, orig.canvas.width, orig.canvas.height);
          var ci = cand.ctx.getImageData(0, 0, cand.canvas.width, cand.canvas.height);
          var union = R.unionWithContent(
            { w: oi.width, h: oi.height, data: oi.data },
            { w: ci.width, h: ci.height, data: ci.data }
          );
          if (!union.applied) {
            console.log('[FORTEE pipeline] union skipped (' + union.reason + '); judging the raw AI result');
            return null;
          }

          var canvas = document.createElement('canvas');
          canvas.width = union.w;
          canvas.height = union.h;
          var ctx = canvas.getContext('2d');
          var out = ctx.createImageData(union.w, union.h);
          out.data.set(union.data);
          ctx.putImageData(out, 0, 0);
          var dataUrl = canvas.toDataURL('image/png');

          var pubkey = self._uploadPubkey();
          if (!pubkey || !window.forteeShared ||
              typeof window.forteeShared.uploadToUploadcare !== 'function') {
            console.warn('[FORTEE pipeline] union built but no uploader; keeping the raw AI result');
            return null;
          }

          return window.forteeShared.uploadToUploadcare(dataUrl, pubkey).then(function (hosted) {
            // Belt and braces on top of uploadToUploadcare's own contract: the
            // ONLY thing allowed out of here is an http(s) url.
            if (typeof hosted !== 'string' || !/^https:\/\//i.test(hosted)) {
              console.warn('[FORTEE pipeline] union upload failed; keeping the raw AI result');
              return null;
            }
            console.log('[FORTEE pipeline] union restored ' + union.restoredPixels +
              ' px at ' + union.w + 'x' + union.h + ' (' + union.tier + ' background)');
            return hosted;
          });
        }).catch(function (err) {
          console.warn('[FORTEE pipeline] union failed; keeping the raw AI result:', err);
          return null;
        });
      },

      // Score a removal result against the customer's original BEFORE it is
      // written anywhere. Resolves { accept, reason, destroyedFraction }.
      //
      // WHY. quick 260819-bb8: the engine is doing salient-object isolation, not
      // background removal. On multi-element artwork it keeps the single biggest
      // object and deletes the rest — measured at 51.6% of real content destroyed
      // on the committed Martel fixture, silently, while the UI told the customer
      // their background had been removed. This is the check that catches it.
      //
      // Both URLs go through the SAME 1200px cap, so the comparison is cheap and
      // the two canvases are the same size. (window.forteeBgRetention samples by
      // nearest neighbour anyway, so a mismatch is scored rather than mis-scored.)
      //
      // ACCEPTS ON ANY FAILURE, deliberately. If the asset is missing, a canvas
      // read is tainted, or scoring throws, this must degrade to today's
      // behaviour — not to an outage that blocks every upload.
      _judgeBgResult: function (cdnUrl, bgUrl) {
        var self = this;
        var unscored = { accept: true, reason: 'unscored', destroyedFraction: 0 };
        var R = (typeof window !== 'undefined') ? window.forteeBgRetention : null;
        if (!R || !cdnUrl || !bgUrl) return Promise.resolve(unscored);

        return Promise.all([
          self._loadImageToCanvas(cdnUrl),
          self._loadImageToCanvas(bgUrl)
        ]).then(function (pair) {
          var orig = pair[0];
          var cand = pair[1];
          if (!orig || !cand) return unscored;
          var oi = orig.ctx.getImageData(0, 0, orig.canvas.width, orig.canvas.height);
          var ci = cand.ctx.getImageData(0, 0, cand.canvas.width, cand.canvas.height);
          return R.judgeCandidate(
            { w: oi.width, h: oi.height, data: oi.data },
            { w: ci.width, h: ci.height, data: ci.data }
          );
        }).catch(function (err) {
          console.warn('[FORTEE pipeline] bg-remove scoring failed; accepting unscored:', err);
          return unscored;
        });
      },

      // Paid remove.bg fallback. Returns Promise<bool> (true = bgRemovedUrl written)
      // so the automatic pipeline + prep screen can await real completion.
      _runBgRemoval: function (groupKey, cdnUrl, filename) {
        var self = this;
        if (!groupKey || !cdnUrl) return Promise.resolve(false);
        // Strip any Uploadcare transformations off the cdnUrl — the remove-bg API
        // expects the bare file ID URL.
        var bareUrl = cdnUrl.replace(/-\/[^/]+\/.*$/, '/');

        self._bgRemovingGroup = groupKey;

        var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        var aborted = false;
        var timeoutId = setTimeout(function () {
          aborted = true;
          if (controller) {
            try { controller.abort(); } catch (e) {}
          }
        }, BG_REMOVE_TIMEOUT_MS);

        var fetchOpts = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileId: bareUrl })
        };
        if (controller) fetchOpts.signal = controller.signal;

        return fetch('https://fortee-api.vercel.app/api/remove-bg', fetchOpts)
          .then(function (r) {
            console.log('[FORTEE pipeline] bg-remove API status:', r.status);
            return r.json();
          })
          .then(function (data) {
            clearTimeout(timeoutId);
            if (data && data.url) {
              // REPAIR FIRST, GUARD SECOND, WRITE THIRD. Until quick 260819-bb8
              // this branch called _applyBgRemoved the instant a url came back,
              // so whatever the engine returned went straight to the customer.
              //
              // _unionBgCandidate repairs the engine's chief failure mode on
              // flat and semi backgrounds by unioning its alpha with the
              // artwork the border profile can see - the elements it discarded.
              // It declines (resolving null) on photographic backgrounds, where
              // the union would restore the background itself, and on any
              // upload or canvas failure. Whichever candidate survives that is
              // the one the guard judges and the one the customer gets: there is
              // exactly one candidateUrl below, so the scored artefact and the
              // written artefact can never diverge.
              //
              // _bgRemovingGroup is cleared inside BOTH terminal branches rather
              // than here: clearing it up front would drop the "removing" state
              // while the verdict is still pending.
              return self._unionBgCandidate(cdnUrl, data.url).then(function (unionUrl) {
                var candidateUrl = unionUrl || data.url;
                return self._judgeBgResult(cdnUrl, candidateUrl).then(function (verdict) {
                  self._bgRemovingGroup = null;
                  if (!verdict.accept) {
                    console.warn('[FORTEE pipeline] bg-remove REJECTED:', verdict.reason, verdict.destroyedFraction);
                    // Non-destructive by construction: the customer keeps their
                    // untouched upload and a human is told why.
                    self._flagNeedsReview(groupKey, verdict.reason);
                    self._buildPrePlacedTiles();
                    return false;
                  }
                  self._applyBgRemoved(groupKey, cdnUrl, filename, candidateUrl);
                  self._bgRemoveSuccess = true;
                  // Something really was stripped, so the reassurance is now true.
                  // Deliberately NOT set on the reject, no-url or catch branches:
                  // an upload whose removal failed OR was rejected still shows its
                  // original background, and originalSafeMode() reads this flag to
                  // decide whether to tell the customer we removed one.
                  self._bgRemovalRan = true;
                  setTimeout(function () { self._bgRemoveSuccess = false; }, 3000);
                  console.log('[FORTEE pipeline] BG removal complete for', groupKey);
                  return true;
                });
              });
            }
            self._bgRemovingGroup = null;
            console.warn('[FORTEE pipeline] bg-remove API returned no url; keeping original');
            self._buildPrePlacedTiles();
            return false;
          })
          .catch(function (err) {
            clearTimeout(timeoutId);
            self._bgRemovingGroup = null;
            console.warn('[FORTEE pipeline] bg-remove API failed:', aborted ? 'timeout' : err);
            self._buildPrePlacedTiles();
            return false;
          });
      },

      // V2: automatic background removal. Already-transparent uploads are handled
      // upstream by the alpha fast-path (never reach here). Non-transparent uploads
      // go straight to the proxy, which tries Photoroom first (~$0.02/image) and only
      // falls back to the Uploadcare remove.bg add-on when Photoroom fails. The free
      // in-browser flood-fill remover was removed (low quality). Returns Promise<bool>
      // so the prep screen awaits real completion.
      _autoRemoveBg: function (groupKey, cdnUrl, filename) {
        var self = this;
        if (!groupKey || !cdnUrl) return Promise.resolve(false);
        self._bgRemovingGroup = groupKey;
        console.log('[FORTEE pipeline] auto bg removal via proxy for', groupKey);
        return self._runBgRemoval(groupKey, cdnUrl, filename);
      },

      // Removal failed OR was rejected by the retention guard → keep the original
      // visible, seed a minimal version set (so previews resolve), and flag for a
      // human. Reasons that reach here:
      //
      //   'bg-removal-failed'             the proxy errored, timed out, or 502'd
      //   'bg-removal-destroyed-content'  the engine deleted real artwork (the
      //                                   51.6% Martel failure — quick 260819-bb8)
      //   'bg-removal-erased-everything'  the engine returned a near-empty frame
      //   'bg-removal-no-op'              the engine returned the input untouched
      _flagNeedsReview: function (groupKey, reason) {
        var a = this._pipelineArtwork(groupKey);
        if (!a) return;
        var src = this._versionSourceUrl(a);
        var next = Object.assign({}, a);
        next.analysis = Object.assign({ needsReview: true, reviewReasons: [] }, a.analysis || {});
        next.analysis.needsReview = true;
        // De-duplicated: _runBgRemoval resolving false makes _processArtwork's
        // handler flag 'bg-removal-failed' a SECOND time on the rejection path,
        // which would otherwise show the reviewer the same reason twice.
        if (reason) {
          var reasons = next.analysis.reviewReasons || [];
          if (reasons.indexOf(reason) === -1) reasons = reasons.concat([reason]);
          next.analysis.reviewReasons = reasons;
        }
        if (!next.versionUrls) next.versionUrls = { original: src };
        next._versionsFor = src;   // stop _generateVersions from recolouring a backgrounded logo
        // No cascade here (the original never cascaded this write) — but the caches
        // and the previews still have to be invalidated, which is what the host's
        // cascade:false branch does.
        this._pipelineWriteArtwork(groupKey, next, { cascade: false });
        console.warn('[FORTEE pipeline] bg removal failed → needs review:', groupKey, reason || '');
      },

      setBgPreference: function (pref) {
        if (pref !== 'original' && pref !== 'transparent') return;
        if (this._bgPreference === pref) return;
        this._bgPreference = pref;
        // Step 2 mockups composite with the chosen background — when the customer
        // flips Original/Transparent, drop the cache so previews re-composite the
        // next time they're requested.
        this._mockup2Cache = {};
      },

      // Sample non-transparent pixels and return true when >5% have luminance > 0.85.
      // Pure dark logos return false → entire color-check block stays hidden. If we
      // can't read pixels we default to true (safer to show the check than to hide it).
      _detectLightTones: function (canvas, ctx) {
        try {
          var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          var indices = [];
          for (var i = 0; i < data.length; i += 4) {
            if (data[i + 3] > 200) indices.push(i);
          }
          if (indices.length === 0) return false;
          var sampleN = Math.min(200, indices.length);
          var step = indices.length / sampleN;
          var light = 0;
          for (var s = 0; s < sampleN; s++) {
            var idx = indices[Math.floor(s * step)];
            var r = data[idx], g = data[idx + 1], b = data[idx + 2];
            // ITU-R BT.709 perceptual luminance, normalized to [0,1]
            var lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
            if (lum > 0.85) light++;
          }
          return (light / sampleN) > 0.05;
        } catch (e) {
          return true;
        }
      },

      // =====================================================================
      // "Logo added" card — one row PER PLACEMENT
      // =====================================================================
      // Each active placement gets its own row: its own view, version, override,
      // review flag and Edit — so front and back can carry different logos.
      _ctxFor: function (productId, locKey) {
        var view = this._viewForLocKey(productId, locKey);
        if (view && !this._pipelineArtwork(view)) {
          // Lazy cascade (mirrors _kickStep2Mockup). Hosts that do NOT share one
          // logo across placements return null here and the branch is inert.
          var pa = this._pipelineCascadeSource(view);
          // quiet: Alpine evaluates these getters during render, so this write
          // must not kick off preview rebuilds or store replacements.
          if (pa) this._pipelineWriteArtwork(view, pa, { quiet: true });
        }
        return this._activeArtworkContext(productId, view);
      },
      _rowVersionKey: function (productId, locKey) {
        var c = this._ctxFor(productId, locKey);
        if (!c || !c.artwork || !c.artwork.versionUrls) return null;
        return this._autoVersionKey(c.product, c.color, c.artwork);
      },
      artRowHasLogo: function (productId, locKey) { var c = this._ctxFor(productId, locKey); return !!(c && c.artwork && (c.artwork.url || c.artwork.bgRemovedUrl)); },
      artRowThumb: function (productId, locKey) {
        var c = this._ctxFor(productId, locKey);
        if (!c || !c.artwork) return null;
        var k = this._rowVersionKey(productId, locKey);
        if (k && c.artwork.versionUrls && c.artwork.versionUrls[k]) return c.artwork.versionUrls[k];
        return this._artworkUrlFromObj(c.artwork);
      },
      artRowVersionLabel: function (productId, locKey) {
        var k = this._rowVersionKey(productId, locKey);
        if (!k) return this.artRowHasLogo(productId, locKey) ? this._t('artwork_preparing', 'Preparing versions…') : '';
        return this._VERSION_LABELS[k] || 'Original';
      },
      // Colour "why" copy + self-serve override affordance deleted (36-02) — the
      // Artwork Card now states colour is optimized by the art team.
      artRowNeedsReview: function (productId, locKey) { var c = this._ctxFor(productId, locKey); return !!(c && c.artwork && c.artwork.analysis && c.artwork.analysis.needsReview); },
      artRowLabel: function (productId, locKey) { return this._rowPlacementLabel(productId, locKey); },
      requestRowReview: function (productId, locKey) {
        var view = this._viewForLocKey(productId, locKey);
        if (view) this._designerReviewRequested[view] = true;
        this.showToast(this._t('artwork_designer_sent', 'Sent to our designer — proof within 24 hours.'), false);
      },

      // Open the Touch up surface on this row (assets/fortee-touchup-ui.js).
      //
      // The row is the only thing that knows how to turn (productId, locKey)
      // into a view, so the resolution happens here rather than inside the
      // touch-up surface - the two builders key artwork differently and the
      // shared surface must not have to care.
      //
      // Guarded on openTouchup rather than assumed: a host that has not loaded
      // fortee-touchup-ui.js degrades to "no touch up" instead of throwing on a
      // button press. Same contract as every other optional module here.
      artRowTouchUp: function (productId, locKey) {
        if (typeof this.openTouchup !== 'function') return;
        var view = this._viewForLocKey(productId, locKey);
        if (view) this.openTouchup(view);
      },
      artRowCanTouchUp: function (productId, locKey) {
        return typeof this.openTouchup === 'function' && this.artRowHasLogo(productId, locKey);
      },

      // Delete affordance on the shared card (USER DECISION 2026-08-07 — kept on
      // BOTH builders). x-show already gates the card on artRowHasLogo, so the
      // guard here is belt-and-braces against a keyboard activation mid-flush.
      artRowDelete: function (productId, locKey) {
        var view = this._viewForLocKey(productId, locKey);
        if (!view || !this._pipelineArtwork(view)) return;
        this._pipelineDeleteArtwork(view);
      },

      // =====================================================================
      // Edit fork (Edit current logo / Upload a new logo / Cancel)
      // =====================================================================
      openArtworkEditor: function (productId, locKey) {
        this._artworkEditFor = productId;
        // Gate the fork on a key rather than a product id — the single builder
        // has no product id and would otherwise never open the dialog.
        this._artworkEditKey = locKey || productId || true;
        this._editorView = locKey ? this._viewForLocKey(productId, locKey) : this._activeViewForProduct(productId);
      },
      closeArtworkEditor: function () { this._artworkEditFor = null; this._artworkEditKey = null; },

      // The fork's two actions, so the dialog closes through ONE path.
      // openEditor() and startUploadNew() both null _artworkEditFor themselves —
      // that used to be what dismissed the fork, back when its x-show was gated on
      // _artworkEditFor. Gating on _artworkEditKey means the fork has to be closed
      // explicitly, and the product id has to be read BEFORE the close clears it.
      forkEditCurrent: function () {
        var pid = this._artworkEditFor;
        this.closeArtworkEditor();
        this.openEditor(pid);
      },
      forkUploadNew: function () {
        var pid = this._artworkEditFor;
        this.closeArtworkEditor();
        this.startUploadNew(pid);
      },

      // The fork's third action. _editorView is what openArtworkEditor already
      // resolved, and it is read BEFORE closeArtworkEditor() for the same reason
      // the product id is above: closing clears the dialog's state.
      forkTouchUp: function () {
        var view = this._editorView ||
          (this._artworkEditFor ? this._activeViewForProduct(this._artworkEditFor) : null);
        this.closeArtworkEditor();
        if (view && typeof this.openTouchup === 'function') this.openTouchup(view);
      },
      forkCanTouchUp: function () {
        return typeof this.openTouchup === 'function';
      },

      // =====================================================================
      // Step 1.5 — "Preparing your artwork" overlay (spec §1.5 / prototype)
      // =====================================================================
      // The checkmarks animate in sequence while the real work — bg detection +
      // version generation — runs in the background. The LEADING steps run on a
      // fixed cadence; the FINAL step spins until the pipeline actually reports
      // ready (_prepMarkReady) and only then completes. That coupling is the
      // whole design: with the animation on its own timer, a fast upload flashes
      // past the list and a slow one leaves a fully-checked list frozen on screen
      // while the customer waits. The Continue button is still gated on BOTH the
      // animation and processing (_prepMaybeDone), with a safety timeout so it
      // can never hang.
      PREP_STEPS: ['Analyzing logo', 'Detecting background', 'Checking print quality', 'Verifying pixels', 'Removing excess background'],
      PREP_STEP_MS: 340,    // cadence of the leading steps
      PREP_SETTLE_MS: 280,  // how long the final tick stays on screen before the ready panel

      _startPrep: function (mode) {
        var self = this;
        this._prepClearTimers();
        this._prepAfter = mode || 'initial';
        this._prepReady = false;
        this._prepAnimDone = false;
        this._prepDone = false;
        this._prepSteps = this.PREP_STEPS.map(function (l) { return { label: l, state: 'pending' }; });
        this._prepOpen = true;
        var per = this.PREP_STEP_MS;
        var last = this._prepSteps.length - 1;
        // Leading steps: unchanged cadence — doing at i*per, done one cadence later.
        for (var i = 0; i < last; i++) {
          (function (idx) {
            self._prepTimers.push(setTimeout(function () { if (self._prepSteps[idx]) self._prepSteps[idx].state = 'doing'; }, idx * per));
            self._prepTimers.push(setTimeout(function () { if (self._prepSteps[idx]) self._prepSteps[idx].state = 'done'; }, idx * per + per));
          }(i));
        }
        // Final step: enters 'doing' on the same cadence and gets NO done timer.
        // _prepMarkReady is what finishes it, so its spinner tracks the real work.
        this._prepTimers.push(setTimeout(function () { if (self._prepSteps[last]) self._prepSteps[last].state = 'doing'; }, last * per));
        this._prepLastStepAt = Date.now() + last * per;
        // Safety net — never trap the customer if processing stalls.
        this._prepTimers.push(setTimeout(function () { self._prepMarkReady(); }, 9000));
      },
      // Called from every real-processing exit path AND from the safety net, so
      // the first signal wins and the rest are no-ops.
      _prepMarkReady: function () {
        var self = this;
        if (this._prepReady) return;
        this._prepReady = true;
        // Floor: never resolve before the final step has had its turn on screen
        // (that is what stops a fast upload skipping the animation).
        // Settle: keep that last tick visible once the work is genuinely done.
        var wait = Math.max(0, this._prepLastStepAt - Date.now()) + this.PREP_SETTLE_MS;
        this._prepTimers.push(setTimeout(function () {
          self._prepSteps.forEach(function (s) { s.state = 'done'; });
          self._prepAnimDone = true;
          self._prepMaybeDone();
        }, wait));
      },
      _prepMaybeDone: function () {
        if (!(this._prepOpen && this._prepReady && this._prepAnimDone)) return;
        if (this._prepAfter === 'newlogo') {
          // Path A (upload a new logo): prep finished → jump straight to the scope
          // question instead of the "ready / continue" panel.
          this._prepClearTimers();
          this._prepOpen = false; this._prepDone = false;
          var a = this._prepUploadGroup ? this._pipelineArtwork(this._prepUploadGroup) : null;
          var url = a ? (a.bgRemovedUrl || a.url) : null;
          // The whole record is the scope's SOURCE, not just its display url.
          // Handing over only the url made applyScope treat a brand-new upload
          // as an edit of the logo it replaced: the old logo stayed on as
          // editedFrom and the cart recorded it as the original (order 7838),
          // and a non-transparent upload lost its raw file to the cutout.
          // The scratch slot stays put so a late cutout still cascades.
          if (url) this._openScope(this._uploadNewProductId, url, a);
        } else {
          this._prepDone = true;
        }
      },
      prepContinue: function () {
        this._prepClearTimers(); this._prepOpen = false; this._prepDone = false;
        // Match the prototype: after "Your logo is ready" go straight to the builder,
        // never back to the Step 1 upload screen. What "the builder" means is
        // host-specific — the 2-step advances its step machine, the single builder
        // just closes the overlay.
        this._prepAfterContinue();
      },
      prepReviewArtwork: function () {
        this._prepClearTimers(); this._prepOpen = false; this._prepDone = false;
        var t = this._prepReviewTarget();
        if (t) this.openArtworkEditor(t.pid, t.key);
      },
      _prepClearTimers: function () {
        this._prepTimers.forEach(function (t) { clearTimeout(t); });
        this._prepTimers = [];
      },
      prepLogoUrl: function () {
        var a = this._pipelinePrimaryArtwork();
        return a ? this._artworkUrlFromObj(a) : null;
      },

      // Which reassurance snippets/fortee-original-safe-note.liquid should show:
      // 'bg' (background stripped), 'trim' (already transparent, only cropped),
      // or '' (preview is a faithful copy — say nothing rather than invent a
      // change that did not happen).
      //
      // 'bg' outranks 'trim' when both are true: a removal is the alteration
      // that alarms people, and its copy already promises the original file is
      // what prints, which covers the crop as well.
      originalSafeMode: function () {
        if (this._bgRemovalRan) return 'bg';
        if (this._artworkTrimmed) return 'trim';
        return '';
      }
    };
  };
})();
